// Western Michigan University: public college page (#2748), official facts only.
//
// Every figure below was read on 2026-10-01 from the source it names:
//  - WMU's Common Data Set 2025-2026 (wmu_cds_2025-26.pdf), linked from the
//    Office of Institutional Research's Common Data Set page (the SOURCES url):
//    C1 applicants, admits and enrollees for Fall 2025 with the residency
//    table, C8 test policy, C21/C22 early plans with the Fall 2025 early
//    action counts. Quirk: C8 marks the SAT or ACT as "not considered" and C8A
//    answers no, while C8F says scores are used only if they help a student;
//    WMU's admissions pages call the policy test-optional, and the page
//    follows them. The CDS G1 figures are 2025-26 placeholders (it checks the
//    box saying 2026-2027 costs were not yet available), so the cost comes
//    from the aid office instead.
//  - WMU Admissions: Important Dates and Deadlines (2027 Application
//    Deadlines, Fall 2027 entry), Apply as a First-Year Undergraduate,
//    Admission Criteria, WMU Testing Policy, Competitive Admission Programs.
//  - WMU Student Financial Aid, Cost of Attendance: the 2026-27 estimated
//    budget for an undergraduate, fall and spring. WMU labels the budget
//    "On/Off Campus" and says the flat-rate tuition in it does not apply to
//    the Haworth College of Business, the College of Engineering and Applied
//    Sciences or the College of Fine Arts, which add differential tuition.
//  - College Scorecard, school id 172699 (net price by family income), read
//    from the Scorecard API on 2026-10-01.
// Nothing here is profile-derived, an odds figure or a student record.
// Re-check a figure against its source before editing it, and move
// `lastChecked` when you do.

export const SOURCES = {
    cds: {
        label: 'WMU Common Data Set 2025-2026',
        url: 'https://wmich.edu/institutionalresearch/commondataset',
        read: '2026-10-01',
    },
    deadlines: {
        label: 'WMU Admissions, Important Dates and Deadlines',
        url: 'https://wmich.edu/apply/deadlines',
        read: '2026-10-01',
    },
    criteria: {
        label: 'WMU Admissions, Admission Criteria',
        url: 'https://wmich.edu/admissions/first-year-apply/criteria',
        read: '2026-10-01',
    },
    tests: {
        label: 'WMU Admissions, WMU Testing Policy',
        url: 'https://wmich.edu/apply/transcripts/testing',
        read: '2026-10-01',
    },
    competitive: {
        label: 'WMU Admissions, Competitive Admission Programs',
        url: 'https://wmich.edu/apply/competitive',
        read: '2026-10-01',
    },
    fineArts: {
        label: 'WMU College of Fine Arts, Admission',
        url: 'https://wmich.edu/finearts/admission',
        read: '2026-10-01',
    },
    costs: {
        label: 'WMU Student Financial Aid, Cost of Attendance',
        url: 'https://wmich.edu/finaid/cost-of-attendance',
        read: '2026-10-01',
    },
    scorecard: {
        label: 'College Scorecard, U.S. Department of Education',
        url: 'https://collegescorecard.ed.gov/school/?172699-Western-Michigan-University',
        read: '2026-10-15',
    },
};

const college = {
    slug: 'western-michigan-university',
    kbId: 'western_michigan_university',
    name: 'Western Michigan University',
    shortName: 'Western Michigan',
    location: 'Kalamazoo, MI',
    control: 'Public',
    lastChecked: '2026-10-01',
    sources: SOURCES,
    // From the KB's sourced branding (WMU visual identity color page); decoration only.
    branding: {
        primary: '#532E1F',
        secondary: '#F1C500',
        logo_url: 'https://storage.googleapis.com/college-counselor-assets/university-logos/western_michigan_university_logo.png',
    },

    admission: {
        entering: 'Fall 2025',
        cdsCycle: '2025-2026',
        applied: 20927,
        admitted: 17853,
        enrolled: 2303,
        source: 'cds',
        where: 'section C1',
        residency: [
            { group: 'In-state', applied: 14374, admitted: 12555 },
            { group: 'Out-of-state', applied: 5745, admitted: 5072 },
            { group: 'International', applied: 808, admitted: 226 },
        ],
        residencySource: 'cds',
        residencyWhere: 'section C1, residency table',
    },
    // Acceptance by school or major: shown only where the school publishes it.
    byMajor: {
        published: false,
        note: 'WMU\'s Common Data Set reports one first-year acceptance rate for the whole university, with no rate by college or major.',
        source: 'cds',
    },
    earlyRounds: {
        title: 'Early action',
        note: 'WMU has no early decision plan. Its early action plan is not binding and not restrictive. The counts are for students who entered in fall 2025.',
        year: 'Fall 2025',
        rows: [
            { plan: 'Early Action', applied: 17691, admitted: 15831 },
        ],
        source: 'cds',
        where: 'sections C21 and C22',
    },

    programs: {
        entering: 'Fall 2027',
        intro: 'You apply to WMU through the Common App. A few programs ask for more:',
        rows: [
            { school: 'Aviation Flight Science', requirement: 'A competitive program with limited seats, fall entry only. Apply by the December 1 early action deadline; recommendation letters, essays and other materials are optional, but WMU says you should send them. Decisions come out in mid-February, and you confirm by May 1.', source: 'competitive' },
            { school: 'Bronson School of Nursing (direct admission)', requirement: 'Direct admission for first-year students is competitive, fall entry only, with the same December 1 deadline, mid-February decisions and May 1 confirmation.', source: 'competitive' },
            { school: 'College of Fine Arts', requirement: 'Art applicants submit a portfolio through SlideRoom; music and dance applicants audition; theatre applicants audition, or interview if they are not performers; Product Design takes a portfolio through SlideRoom for initial acceptance.', source: 'fineArts' },
        ],
        source: 'competitive',
    },

    deadlines: {
        entering: 'Fall 2027',
        headline: { label: 'Final deadline', value: 'June 1' },
        rows: [
            { plan: 'Early Action', deadline: 'December 1', decision: 'After winter break', binding: false },
            { plan: 'Rolling admission', deadline: 'June 1', decision: 'Six to eight weeks after your file is complete', binding: false },
        ],
        note: 'WMU accepts first-year applications only through the Common App. Application review begins in October. Applying by the December 1 early action deadline puts you in line for admission to every WMU program, admissions merit scholarships, Medallion scholarship consideration and a priority invitation to the Lee Honors College. Application materials must arrive by the deadline.',
        source: 'deadlines',
    },

    testing: {
        entering: 'Fall 2027',
        policy: 'Test-optional',
        blind: false,
        line: 'WMU is test-optional for admission and for Office of Admissions scholarships. If you want your scores used, self-report them on the application and report every test date; WMU superscores the SAT and the ACT, and says a score will not disadvantage you. If a score was used in your admission, you send official scores before orientation.',
        source: 'tests',
        cdsLine: 'The Common Data Set marks the SAT or ACT as not considered for fall 2027 applicants, and adds that scores are used only if they help a student.',
        cdsWhere: 'section C8',
    },

    // The next cycle (2027-28, fall 2028 entry): only what the school has
    // announced, read on the date below; nothing announced says so (#2651).
    nextCycle: {
        entering: 'Fall 2028',
        checked: '2026-10-01',
        changes: [],
        source: 'criteria',
    },

    cost: {
        year: '2026-2027',
        columns: ['In-state', 'Out-of-state'],
        rows: [
            { item: 'Tuition', amounts: [16449, 20525] },
            { item: 'Standard fees', amounts: [140, 140] },
            { item: 'Housing and food', amounts: [14590, 14590] },
            { item: 'Books and supplies', amounts: [1128, 1128] },
            { item: 'Transportation', amounts: [2880, 2880] },
            { item: 'Loan fees', amounts: [37, 37] },
            { item: 'Other expenses', amounts: [2046, 2046] },
        ],
        totals: [37270, 41346],
        note: 'WMU\'s estimated budget for an undergraduate, fall and spring, on or off campus. Business, engineering and fine arts majors pay differential tuition on top of the flat rate.',
        source: 'costs',
        netPrice: {
            average: 13902,
            byIncome: [
                { income: '$0 to $30,000', amount: 5317 },
                { income: '$30,001 to $48,000', amount: 5256 },
                { income: '$48,001 to $75,000', amount: 10093 },
                { income: '$75,001 to $110,000', amount: 15678 },
                { income: 'Over $110,000', amount: 23166 },
            ],
            note: 'What first-year, full-time, in-state students who received federal aid paid after grants and scholarships, by family income, in 2024-25, the latest year the Department of Education publishes. Your own price comes from WMU\'s aid offer.',
            tileDetail: 'Per year, after grants, in-state federal aid recipients',
            source: 'scorecard',
        },
    },
};

export default college;
